import { AISearchApi } from 'api';
import React, { createContext, useContext, useState } from 'react';
import { getGASessionId } from 'utils/analytics';

export interface IAISearchContext {
  isLoading: boolean;
  hasError: boolean;
  resetError: () => void;
  aiSearchResult: string | null;
  aiSearchId: string | null;
  getAISearchResult: (query: string) => Promise<string | null>;
}

interface AISearchProviderProps {
  children: JSX.Element;
}

const AISearchContext = createContext<IAISearchContext | undefined>(undefined);

const useAISearchContext = () => useContext(AISearchContext) as IAISearchContext;

const AISearchProvider = ({ children }: AISearchProviderProps) => {
  const [isLoading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [aiSearchResult, setAISearchResult] = useState<string | null>('');
  const [aiSearchId, setAISearchId] = useState<string | null>('');

  const getAISearchResult = async (query: string): Promise<string | null> => {
    setLoading(true);
    try {
      const sessionId = getGASessionId();
      const response = await AISearchApi.getSearchResult(query, sessionId);
      const searchId = response?.data?.search_id || null;
      setAISearchResult(response?.data?.message || null);
      setAISearchId(searchId);
      return searchId;
    } catch (e) {
      setHasError(true);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const resetError = () => {
    setAISearchResult('');
    setHasError(false);
  };

  return (
    <AISearchContext.Provider
      value={{
        isLoading,
        aiSearchResult,
        aiSearchId,
        hasError,
        resetError,
        getAISearchResult,
      }}>
      {children}
    </AISearchContext.Provider>
  );
};

export { AISearchContext, AISearchProvider, useAISearchContext };
